import { useEffect, useRef, useState } from "react";
import { adminApi, type DeliveryInput } from "@/lib/api";
import { STATUS_META, money, fmtDate } from "@/lib/order";
import { printOrderSheet } from "@/lib/printOrder";
import DeliveryPicker from "@/components/orders/DeliveryPicker";

const STATUSES = ["draft", "quoted", "accepted", "paid", "in_production", "shipped", "complete", "cancelled"];

export default function OrderDrawer({
  orderId,
  onClose,
  onChanged,
}: {
  orderId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<{ order: any; items: any[]; files: any[]; payments: any[] } | null>(null);
  const [quote, setQuote] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [editDelivery, setEditDelivery] = useState(false);
  const [delivery, setDelivery] = useState<DeliveryInput>({});
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    const r = await adminApi.order(orderId);
    setData(r);
    setQuote(r.order.quote_amount != null ? String(r.order.quote_amount) : "");
    setAdminNote(r.order.admin_note || "");
    setDelivery({
      method: r.order.delivery_method || "courier",
      phone: r.order.delivery_phone || "",
      address: r.order.delivery_address || "",
      locker_code: r.order.locker_code || "",
      locker_name: r.order.locker_name || "",
    });
  }
  useEffect(() => {
    load().catch((e) => setErr(e.message));
  }, [orderId]);

  async function run(key: string, fn: () => Promise<any>) {
    setBusy(key);
    setErr("");
    try {
      await fn();
      await load();
      onChanged();
    } catch (e: any) {
      setErr(e.message || "Action failed");
    } finally {
      setBusy("");
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_000_000) {
      setErr("File too large — max ~2MB per file for now.");
      return;
    }
    const dataUrl: string = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result as string);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    await run("upload", () => adminApi.uploadOrderFile(orderId, { filename: file.name, content_type: file.type, data: dataUrl }));
    if (fileRef.current) fileRef.current.value = "";
  }

  async function viewFile(fid: string) {
    const { file } = await adminApi.orderFile(orderId, fid);
    const w = window.open("", "_blank");
    if (w) w.document.write(`<img src="${file.data}" style="max-width:100%"/><p>${file.filename}</p>`);
  }

  async function downloadFile(fid: string) {
    const { file } = await adminApi.orderFile(orderId, fid);
    const a = document.createElement("a");
    a.href = file.data;
    a.download = file.filename || "print-file";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  if (!data) {
    return (
      <Shell onClose={onClose}>
        <p className="text-gray-400">{err || "Loading…"}</p>
      </Shell>
    );
  }
  const { order, items, files, payments } = data;
  const meta = STATUS_META[order.status] ?? STATUS_META.draft;
  const paid = order.payment_status === "paid";

  return (
    <Shell onClose={onClose}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-white">{order.order_number}</h2>
          <p className="text-gray-400 text-sm">
            {order.user_name || ""} · {order.user_email} · {fmtDate(order.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {paid && <span className="rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-1 text-xs font-bold">PAID</span>}
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${meta.className}`}>{meta.label}</span>
        </div>
      </div>

      {err && <p className="mt-3 text-rose-400 text-sm">{err}</p>}

      {/* Quote + pay */}
      <Section title="Quote & payment">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-400">
            Quote amount (R)
            <input
              type="number"
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              className="mt-1 block w-40 rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2 text-white"
            />
          </label>
          <button
            onClick={() => run("quote", () => adminApi.quoteOrder(orderId, Number(quote) || 0))}
            disabled={busy === "quote"}
            className="rounded-lg bg-sky-500 px-4 py-2 font-semibold text-white hover:bg-sky-400 disabled:opacity-60"
          >
            {order.quote_amount != null ? "Update quote" : "Send quote"}
          </button>
          {paid ? (
            <button
              onClick={() => run("unpay", () => adminApi.unpayOrder(orderId))}
              disabled={busy === "unpay"}
              className="rounded-lg bg-rose-500/20 text-rose-300 px-4 py-2 font-semibold hover:bg-rose-500/30"
            >
              Unmark paid
            </button>
          ) : (
            <button
              onClick={() => run("pay", () => adminApi.payOrder(orderId, { amount: Number(quote) || undefined }))}
              disabled={busy === "pay"}
              className="rounded-lg bg-emerald-500 px-4 py-2 font-semibold text-white hover:bg-emerald-400 disabled:opacity-60"
            >
              Mark as paid
            </button>
          )}
        </div>
        {payments.length > 0 && (
          <div className="mt-3 text-sm text-gray-400">
            {payments.map((p, i) => (
              <div key={i}>
                {fmtDate(p.created_at)} · {p.method} · <span className="text-emerald-300">{money(p.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Status */}
      <Section title="Status">
        <select
          value={order.status}
          onChange={(e) => run("status", () => adminApi.setOrderStatus(orderId, e.target.value))}
          className="rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2 text-white"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_META[s]?.label ?? s}
            </option>
          ))}
        </select>
        {paid && <span className="ml-3 text-xs text-gray-500">Paid orders are locked to the customer; you can still edit here.</span>}
      </Section>

      {/* Items */}
      <Section title={`Items (${items.length})`}>
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-3">
              <div className="h-12 w-12 rounded bg-[#0b111c] flex items-center justify-center overflow-hidden shrink-0">
                {it.thumbnail ? <img src={it.thumbnail} className="max-h-full max-w-full object-contain" /> : "🎨"}
              </div>
              <div className="flex-1 text-sm">
                <p className="text-white">{it.description}</p>
                <p className="text-gray-500 text-xs flex items-center gap-1.5">
                  {it.kind === "gangsheet" ? "Gang Sheet" : "T-Shirt"} · Qty {it.qty}
                  {it.garment_color && (
                    <>
                      ·
                      <span className="inline-block h-3 w-3 rounded-full border border-white/30" style={{ backgroundColor: it.garment_color }} />
                      {it.garment_color}
                    </>
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Print files (owner only) */}
      <Section title={`Print files (${files.length}) — internal only`}>
        <div className="flex items-center gap-3 mb-3">
          <label className="cursor-pointer rounded-lg bg-sky-500/20 text-sky-300 px-4 py-2 text-sm font-semibold hover:bg-sky-500/30">
            Upload file
            <input ref={fileRef} type="file" onChange={onFile} className="hidden" />
          </label>
          <span className="text-xs text-gray-500">Customers never see these.</span>
        </div>
        <div className="space-y-1">
          {files.map((f) => (
            <div key={f.id} className="flex items-center justify-between text-sm bg-[#0b111c] rounded-lg px-3 py-2">
              <span className="text-gray-300">
                {f.filename} <span className="text-gray-600">({Math.round((f.size || 0) / 1024)} KB)</span>
              </span>
              <span className="flex gap-3">
                <button onClick={() => viewFile(f.id)} className="text-sky-300 hover:underline">
                  View
                </button>
                <button onClick={() => downloadFile(f.id)} className="text-emerald-300 hover:underline">
                  Download
                </button>
                <button
                  onClick={() => run("delfile", () => adminApi.deleteOrderFile(orderId, f.id))}
                  className="text-rose-300 hover:underline"
                >
                  Delete
                </button>
              </span>
            </div>
          ))}
          {files.length === 0 && <p className="text-gray-500 text-sm">No print files yet.</p>}
        </div>
      </Section>

      {/* Delivery */}
      <Section title="Delivery">
        {editDelivery ? (
          <div className="space-y-3">
            <DeliveryPicker value={delivery} onChange={setDelivery} />
            <div className="flex gap-3">
              <button onClick={() => setEditDelivery(false)} className="text-gray-400 text-sm">
                Cancel
              </button>
              <button
                onClick={() =>
                  run("delivery", async () => {
                    await adminApi.updateOrder(orderId, {
                      delivery_method: delivery.method,
                      delivery_phone: delivery.phone,
                      delivery_address: delivery.address,
                      locker_code: delivery.locker_code,
                      locker_name: delivery.locker_name,
                    });
                    setEditDelivery(false);
                  })
                }
                className="text-sky-300 text-sm font-semibold"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <p className="text-gray-300 text-sm">
              {order.delivery_method === "pudo"
                ? `📦 ${order.locker_name} (${order.locker_code}) · ${order.delivery_phone || "—"}`
                : order.delivery_method === "courier"
                ? `🚚 ${order.delivery_address || "—"} · ${order.delivery_phone || "—"}`
                : "Not specified"}
            </p>
            <button onClick={() => setEditDelivery(true)} className="text-sky-300 text-sm hover:underline">
              Edit
            </button>
          </div>
        )}
      </Section>

      {/* Internal note */}
      <Section title="Internal note">
        <textarea
          rows={2}
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
          className="w-full rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2 text-white text-sm"
        />
        <button
          onClick={() => run("note", () => adminApi.updateOrder(orderId, { admin_note: adminNote }))}
          className="mt-2 text-sky-300 text-sm font-semibold hover:underline"
        >
          Save note
        </button>
      </Section>

      <div className="mt-6 flex gap-3">
        <button
          onClick={() => printOrderSheet(order, items, files)}
          className="rounded-lg bg-white text-slate-900 px-5 py-2.5 font-bold hover:bg-gray-200"
        >
          🖨 Print / Save as PDF
        </button>
        <button onClick={onClose} className="rounded-lg bg-slate-700 text-white px-5 py-2.5 font-semibold hover:bg-slate-600">
          Close
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/60" onClick={onClose}>
      <div
        className="h-full w-full max-w-2xl overflow-y-auto bg-[#0d1524] border-l border-sky-500/20 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-6 border-t border-sky-500/10 pt-5">
      <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">{title}</h3>
      {children}
    </div>
  );
}
